import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import myConfig from "../../configs/config";
import { windFlowDegrees } from "./dashboardWindDirection";
import "./DashboardDailyForecastColumn.css";

const REFRESH_MS = 10 * 60 * 1000;
const MAX_DAYS = 7;

function getFarmLatLng(farm) {
  if (!farm) return null;
  const centroid = farm.centroid || {};
  let lat = Number(centroid.lat);
  let lng = Number(centroid.lng);

  if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) {
    lat = Number(String(farm.latitude != null ? farm.latitude : "").replace(",", "."));
    lng = Number(String(farm.longitude != null ? farm.longitude : "").replace(",", "."));
  }

  if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) {
    const geo = farm.geo_coords;
    if (Array.isArray(geo) && geo.length >= 2) {
      lng = Number(geo[0]);
      lat = Number(geo[1]);
    } else if (geo && typeof geo === "object") {
      lat = Number(geo.lat != null ? geo.lat : geo.y);
      lng = Number(geo.lng != null ? geo.lng : geo.x);
    }
  }

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

function coordsMoved(prev, next) {
  if (!prev || !next) return true;
  return Math.abs(prev.lat - next.lat) >= 0.0008 || Math.abs(prev.lng - next.lng) >= 0.0008;
}

function weatherKey() {
  const match = String(myConfig.WEATHER_DOMAIN_API || "").match(/appid=([^&]+)/);
  return match ? match[1] : "";
}

function formatDayLabel(date, language) {
  const locale = String(language || "").toLowerCase().startsWith("en") ? "en-US" : "pt-BR";
  const weekday = date
    .toLocaleDateString(locale, { weekday: "short" })
    .replace(".", "")
    .toLowerCase();
  return `${weekday} ${date.getDate()}`;
}

function fromOneCallDaily(daily, language) {
  if (!Array.isArray(daily) || !daily.length) return [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return daily
    .map((item) => {
      const at = new Date(Number(item.dt) * 1000);
      if (!Number.isFinite(at.getTime())) return null;
      const dayStart = new Date(at);
      dayStart.setHours(0, 0, 0, 0);
      if (dayStart.getTime() <= today.getTime()) return null;
      const temp = item.temp || {};
      const pop = Number(item.pop);
      return {
        key: dayStart.toISOString().slice(0, 10),
        label: formatDayLabel(at, language),
        temp: Number.isFinite(Number(temp.max))
          ? Math.round(temp.max)
          : Number.isFinite(Number(temp.day))
          ? Math.round(temp.day)
          : null,
        windKmh: Number.isFinite(Number(item.wind_speed))
          ? Math.round(Number(item.wind_speed) * 3.6)
          : null,
        windDeg: Number(item.wind_deg),
        rainChance: Number.isFinite(pop) ? Math.round(pop * 100) : null,
        humidity: Number.isFinite(Number(item.humidity))
          ? Math.round(Number(item.humidity))
          : null,
      };
    })
    .filter(Boolean)
    .slice(0, MAX_DAYS);
}

function fromThreeHourList(list, language) {
  if (!Array.isArray(list) || !list.length) return [];
  const byDay = {};
  list.forEach((item) => {
    const day = String(item.dt_txt || "").slice(0, 10);
    if (!day) return;
    if (!byDay[day]) byDay[day] = [];
    byDay[day].push(item);
  });

  const today = new Date().toISOString().slice(0, 10);
  return Object.keys(byDay)
    .filter((day) => day > today)
    .slice(0, MAX_DAYS)
    .map((day) => {
      const items = byDay[day];
      const temps = items
        .map((item) => Number(item.main && item.main.temp))
        .filter(Number.isFinite);
      const pops = items.map((item) => Number(item.pop)).filter(Number.isFinite);
      const humidities = items
        .map((item) => Number(item.main && item.main.humidity))
        .filter(Number.isFinite);
      const midday =
        items.find((item) => String(item.dt_txt).includes("12:00:00")) ||
        items[Math.floor(items.length / 2)];
      const wind = (midday && midday.wind) || {};
      const at = new Date(`${day}T12:00:00`);
      return {
        key: day,
        label: formatDayLabel(at, language),
        temp: temps.length ? Math.round(Math.max.apply(null, temps)) : null,
        windKmh: Number.isFinite(Number(wind.speed))
          ? Math.round(Number(wind.speed) * 3.6)
          : null,
        windDeg: Number(wind.deg),
        rainChance: pops.length
          ? Math.round(Math.max.apply(null, pops) * 100)
          : null,
        humidity: humidities.length
          ? Math.round(
              humidities.reduce((sum, value) => sum + value, 0) /
                humidities.length
            )
          : null,
      };
    });
}

class DashboardDailyForecastColumn extends Component {
  constructor(props) {
    super(props);
    this.state = { days: [] };
    this.refreshTimer = null;
    this.fetchSeq = 0;
    this.oneCallAvailable = null;
  }

  getQueryCoords() {
    const mapCenter = this.props.mapCenter;
    if (
      mapCenter &&
      Number.isFinite(Number(mapCenter.lat)) &&
      Number.isFinite(Number(mapCenter.lng))
    ) {
      return { lat: Number(mapCenter.lat), lng: Number(mapCenter.lng) };
    }
    return getFarmLatLng(this.props.farm);
  }

  componentDidMount() {
    this.loadForecast();
  }

  componentDidUpdate(prevProps) {
    const prev = prevProps.mapCenter || getFarmLatLng(prevProps.farm);
    const next = this.getQueryCoords();
    if (!next) return;
    if (coordsMoved(prev, next)) {
      this.loadForecast();
    }
  }

  componentWillUnmount() {
    if (this.refreshTimer) {
      window.clearInterval(this.refreshTimer);
    }
  }

  loadForecast = async () => {
    const coords = this.getQueryCoords();
    if (!coords) return;
    const fetchSeq = ++this.fetchSeq;
    const language = this.props.i18n && this.props.i18n.language;
    const key = weatherKey();
    const query = `lat=${coords.lat}&lon=${coords.lng}&units=metric&lang=pt_br&appid=${key}`;

    try {
      let days = [];
      if (this.oneCallAvailable !== false) {
        const oneCallRes = await fetch(
          `https://api.openweathermap.org/data/3.0/onecall?${query}&exclude=minutely,hourly,alerts`
        );
        this.oneCallAvailable = oneCallRes.ok;
        if (oneCallRes.ok) {
          const json = await oneCallRes.json();
          days = fromOneCallDaily(json.daily, language);
        }
      }

      if (!days.length) {
        const forecastRes = await fetch(
          `${myConfig.WEATHER_DOMAIN_API.replace("/weather?", "/forecast?")}&lat=${
            coords.lat
          }&lon=${coords.lng}&lang=pt_br`
        );
        if (!forecastRes.ok) return;
        const json = await forecastRes.json();
        days = fromThreeHourList(json.list, language);
      }

      if (fetchSeq !== this.fetchSeq) return;
      this.setState({ days });
      if (this.refreshTimer) {
        window.clearInterval(this.refreshTimer);
      }
      this.refreshTimer = window.setInterval(() => this.loadForecast(), REFRESH_MS);
    } catch (error) {
      // keep last days
    }
  };

  render() {
    const { t } = this.props;
    const { days } = this.state;
    if (!days.length) return null;

    return (
      <div
        className="dashboard-daily-forecast"
        aria-label={t("Previsão dos próximos dias")}
      >
        {days.map((day) => (
          <div
            key={day.key}
            className="dashboard-daily-forecast__cell"
            title={`${day.label} · ${
              day.temp != null ? `${day.temp}°` : "--"
            } · ${day.windKmh != null ? `${day.windKmh} km/h` : "--"} · ${
              day.rainChance != null
                ? `${day.rainChance}% ${t("chuva")}`
                : `-- ${t("chuva")}`
            } · ${
              day.humidity != null
                ? `${day.humidity}% ${t("Umidade do ar")}`
                : `-- ${t("Umidade do ar")}`
            }`}
          >
            <span className="dashboard-daily-forecast__day">{day.label}</span>
            <span
              className="dashboard-daily-forecast__arrow"
              style={{
                transform: `rotate(${windFlowDegrees(day.windDeg)}deg)`,
              }}
            >
              ↑
            </span>
            <span className="dashboard-daily-forecast__wind">
              {day.windKmh != null ? `${day.windKmh}` : "--"}
            </span>
            <span className="dashboard-daily-forecast__temp">
              {day.temp != null ? `${day.temp}°` : "--"}
            </span>
            <span className="dashboard-daily-forecast__rain">
              {day.rainChance != null ? `${day.rainChance}%` : "--"}
            </span>
            <span className="dashboard-daily-forecast__humidity">
              {day.humidity != null ? `${day.humidity}%` : "--"}
            </span>
          </div>
        ))}
      </div>
    );
  }
}

export default withTranslation()(DashboardDailyForecastColumn);
